import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Link,
  Tooltip,
  IconButton,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useMutation } from '@apollo/client';
import { PlanStep, RequestThreadStatus, StepRequestThread } from '../types/generated/graphql';
import { CREATE_REQUEST_THREAD, DELETE_STEP_REQUEST_THREAD, GET_PLAN } from '../graphql/queries';
import { generateFieldsFromStep } from '../utils/step-field-generator';

// Debug mode - only in development
const DEBUG_MODE = process.env.NODE_ENV === 'development';

interface StepRequestThreadPanelProps {
  planId: string;
  step: PlanStep;
  onThreadCreated?: () => void;
}

type DelegateMethod = 'email' | 'sms';

// Get status display info
const getStatusInfo = (status: RequestThreadStatus) => {
  switch (status) {
    case RequestThreadStatus.Satisfied:
      return { label: 'Completed', color: 'success', icon: DoneAllIcon };
    case RequestThreadStatus.Ready:
      return { label: 'Ready', color: 'info', icon: CheckCircleIcon };
    case RequestThreadStatus.Open:
      return { label: 'Pending', color: 'warning', icon: PendingIcon };
    default:
      return { label: status, color: 'default', icon: PendingIcon };
  }
};

/**
 * Panel for delegating tasks to others via email or SMS.
 * Creates a shareable chat link for collecting information via conversation.
 */
export const StepRequestThreadPanel: React.FC<StepRequestThreadPanelProps> = ({
  planId,
  step,
  onThreadCreated,
}) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);

  // Delegation form state
  const [delegateMethod, setDelegateMethod] = useState<DelegateMethod>('email');
  const [delegateContact, setDelegateContact] = useState('');
  const [delegateSent, setDelegateSent] = useState(false);

  // Get existing threads from step (array)
  const requestThreads = step.requestThreads || [];
  const hasThreads = requestThreads.length > 0;

  // Mutation to create thread
  const [createThread, { loading: creating, error }] = useMutation(CREATE_REQUEST_THREAD, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setCreatedUrl(data.createRequestThread.publicUrl);
      setDelegateSent(true);
      onThreadCreated?.();
    },
  });

  // Mutation to delete thread
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteThread, { loading: deleting, error: deleteError }] = useMutation(DELETE_STEP_REQUEST_THREAD, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setDeletingThreadId(null);
    },
    onError: () => {
      setDeletingThreadId(null);
    },
  });

  const handleDelegate = async () => {
    if (!delegateContact.trim()) {
      return;
    }

    // Build a focused ask based on the step title
    // The step title IS the primary objective (Golden Rule)
    const ask = `${step.title}`;

    // Use step description if available, otherwise generate helpful context
    const description = step.description || `Please confirm when you have completed: ${step.title}`;

    // Prefer step.allowedFields when present (canonical step form); else generate from title/description
    const generated = generateFieldsFromStep(step.title, step.description);
    const useStepFields = step.allowedFields && step.allowedFields.length > 0;
    const fields = useStepFields
      ? step.allowedFields!.map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          required: f.required,
          description: f.description ?? undefined,
          enumValues: f.enumValues ?? undefined,
        }))
      : generated.fields;
    const outputContract = useStepFields
      ? { allowedKeys: step.allowedFields!.map((f) => f.key) }
      : generated.outputContract;
    const uiMode = useStepFields ? ('form' as const) : generated.uiMode;

    try {
      await createThread({
        variables: {
          input: {
            parentPlanId: planId,
            parentStepId: step.id,
            requestSpec: {
              ask,
              description,
              allowedFields: fields,
              outputContract,
              uiMode,
            },
            accessMode: 'PUBLIC',
            // Include delegate info for future notification support
            delegateTo: {
              method: delegateMethod,
              contact: delegateContact.trim(),
            },
          },
        },
      });

      // TODO: Backend should send notification via email/SMS
      // For now, we just create the thread and show the link in debug mode

    } catch (err) {
      console.error('Failed to delegate task:', err);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleOpenLink = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDeleteThread = async (threadId: string) => {
    if (!window.confirm('Are you sure you want to delete this delegation?')) {
      return;
    }
    setDeletingThreadId(threadId);
    try {
      await deleteThread({
        variables: {
          planId,
          stepId: step.id,
          threadId,
        },
      });
    } catch (err) {
      console.error('Failed to delete delegation:', err);
    }
  };

  const handleNewDelegation = () => {
    setCreatedUrl(null);
    setDelegateSent(false);
    setDelegateContact('');
  };

  const isValidContact = () => {
    const contact = delegateContact.trim();
    if (!contact) return false;

    if (delegateMethod === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);
    } else {
      // Basic phone validation - at least 10 digits
      return /^\+?[\d\s-()]{10,}$/.test(contact);
    }
  };

  // Render a single thread card
  const renderThreadCard = (thread: StepRequestThread, isNew = false) => {
    const statusInfo = getStatusInfo(thread.status);
    const StatusIcon = statusInfo.icon;
    const isSatisfied = thread.status === RequestThreadStatus.Satisfied;
    const isDeleting = deletingThreadId === thread.threadId;

    return (
      <Paper
        key={thread.threadId}
        elevation={0}
        sx={{
          p: 2,
          bgcolor: isSatisfied ? 'success.light' : 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: isSatisfied ? 'success.main' : 'grey.300',
          mb: 1,
          opacity: isDeleting ? 0.5 : 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PersonAddIcon sx={{ color: isSatisfied ? 'success.dark' : 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isSatisfied ? 'success.dark' : 'inherit' }}>
            Delegated Task {isNew && '(Just Sent)'}
          </Typography>
          <Chip
            icon={<StatusIcon sx={{ fontSize: 14 }} />}
            label={statusInfo.label}
            size="small"
            color={statusInfo.color as any}
            sx={{ ml: 'auto' }}
          />
          <Tooltip title="Delete delegation">
            <IconButton
              size="small"
              onClick={() => handleDeleteThread(thread.threadId)}
              disabled={isDeleting}
              sx={{ color: 'error.main' }}
            >
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Show result if satisfied */}
        {isSatisfied && thread.result && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.main', borderRadius: 1, color: 'white' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              ✓ Response Received
            </Typography>
            <Typography variant="body2">
              {thread.result.summary || JSON.stringify(thread.result.fields)}
            </Typography>
          </Box>
        )}

        {/* Show link for non-satisfied threads (always in debug mode, otherwise just status) */}
        {!isSatisfied && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Waiting for delegate to respond...
            </Typography>
            {DEBUG_MODE && (
              <Box sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                p: 1,
                bgcolor: 'warning.light',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'warning.main',
              }}>
                <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                  DEBUG:
                </Typography>
                <Link
                  href={thread.publicUrl}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                >
                  {thread.publicUrl}
                </Link>
                <Tooltip title={copied === thread.publicUrl ? 'Copied!' : 'Copy link'}>
                  <IconButton size="small" onClick={() => handleCopyLink(thread.publicUrl)}>
                    {copied === thread.publicUrl ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <IconButton size="small" onClick={() => handleOpenLink(thread.publicUrl)}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Show existing delegations */}
      {hasThreads && (
        <Box sx={{ mb: 2 }}>
          {requestThreads.map((thread) => renderThreadCard(thread))}
        </Box>
      )}

      {/* Show success message with link in debug mode */}
      {delegateSent && createdUrl && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'success.light',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
              Delegation Sent!
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {delegateMethod === 'email'
              ? `An email has been sent to ${delegateContact}`
              : `A text message has been sent to ${delegateContact}`
            }
          </Typography>

          {DEBUG_MODE && (
            <Box sx={{
              p: 1.5,
              bgcolor: 'warning.light',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'warning.main',
              mb: 2,
            }}>
              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                DEBUG MODE - Delegation Link:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Link href={createdUrl} target="_blank" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                  {createdUrl}
                </Link>
                <IconButton size="small" onClick={() => handleCopyLink(createdUrl)}>
                  {copied === createdUrl ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={() => handleOpenLink(createdUrl)}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={handleNewDelegation}
            startIcon={<PersonAddIcon />}
          >
            Delegate to Someone Else
          </Button>
        </Paper>
      )}

      {/* Delegate form */}
      {!delegateSent && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonAddIcon sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {hasThreads ? 'Delegate to Another Person' : 'Delegate This Task'}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send this task to someone else to complete. They'll receive a link to provide the information you need.
          </Typography>

          {/* Method toggle */}
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={delegateMethod}
              exclusive
              onChange={(_, value) => value && setDelegateMethod(value)}
              size="small"
              fullWidth
            >
              <ToggleButton value="email">
                <EmailIcon sx={{ mr: 1 }} />
                Email
              </ToggleButton>
              <ToggleButton value="sms">
                <SmsIcon sx={{ mr: 1 }} />
                SMS
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Contact input */}
          <TextField
            fullWidth
            size="small"
            label={delegateMethod === 'email' ? 'Email Address' : 'Phone Number'}
            placeholder={delegateMethod === 'email' ? 'someone@example.com' : '+1 (555) 123-4567'}
            value={delegateContact}
            onChange={(e) => setDelegateContact(e.target.value)}
            type={delegateMethod === 'email' ? 'email' : 'tel'}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: delegateMethod === 'email'
                ? <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                : <SmsIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />

          {(error || deleteError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.message || deleteError?.message}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleDelegate}
            disabled={creating || !isValidContact()}
            fullWidth
          >
            {creating ? 'Sending...' : 'Send Delegation'}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default StepRequestThreadPanel;
